/**
 * YourCricketCard — Iter XX.156 landing hero object.
 *
 * Founder rule: the visitor should think "I'm building something
 * about my cricket" — not "I'm starting another chat" — within the
 * first 3 seconds of landing. This card is the visual centre of
 * gravity that carries that meaning.
 *
 * Data source: `GET /api/web/journey` (existing endpoint from
 * Iter XX.56, extended in XX.156 with `lessons_count`). No parallel
 * profile system. No writes. No fabricated numbers.
 *
 * Rules honoured:
 *   · If the endpoint returns is_linked=false OR every count is 0,
 *     render the empty state ("Nothing built yet.") — never a
 *     "profile incomplete" nudge.
 *   · Never surface confidence scores, tags, classifier labels or
 *     internal memory field names.
 *   · Practices count comes from `reflections.intent="practice"` —
 *     already the source of truth used elsewhere. Included because
 *     it has a defensible existing count.
 *   · "Patterns Discovered" intentionally omitted this iteration
 *     (no single defensible source; founder rule = leave it out
 *     rather than invent one).
 *
 * Analytics:
 *   · your_cricket_card_viewed — fires once, when card is ≥50% in
 *     view for ≥1s (deferred so we don't over-count above-the-fold).
 */
import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { track } from "../lib/clarity";

const _EMPTY = {
  matches: 0,
  practices: 0,
  videos: 0,
  conversations: 0,
  lessons: 0,
  current_focus: null,
  is_linked: false,
  empty: true,
};

function _extractCounts(payload) {
  if (!payload || typeof payload !== "object") return _EMPTY;
  const trio = Array.isArray(payload.trio) ? payload.trio : [];
  const findLabel = (needle) => {
    const row = trio.find((r) => (r?.label || "").toLowerCase().includes(needle));
    return row ? Number(row.value) || 0 : 0;
  };
  return {
    matches: findLabel("match"),
    practices: findLabel("practice"),
    videos: findLabel("video"),
    conversations: Number(payload?.hero_metric?.value) || 0,
    lessons: Number(payload?.lessons_count) || 0,
    current_focus: payload?.current_focus || null,
    is_linked: Boolean(payload?.is_linked),
    empty: Boolean(payload?.empty),
  };
}

export default function YourCricketCard({ sessionId, hideWhenEmpty = false }) {
  const [counts, setCounts] = useState(_EMPTY);
  const [loaded, setLoaded] = useState(false);
  const cardRef = useRef(null);
  const viewedRef = useRef(false);

  // Fetch existing /web/journey. Fails silently — falls back to
  // the empty state, never blocks the page or the composer.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = sessionId ? { session_id: sessionId } : {};
        const r = await api.get("/web/journey", { params });
        if (cancelled) return;
        setCounts(_extractCounts(r?.data));
      } catch {
        if (!cancelled) setCounts(_EMPTY);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Fire `your_cricket_card_viewed` once, deferred, when ≥50% of the
  // card is on-screen for ≥1s. Uses IntersectionObserver — no
  // scroll listeners, no re-renders.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let timer = null;
    const io = new IntersectionObserver((entries) => {
      const on = entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.5;
      if (on && !timer && !viewedRef.current) {
        timer = window.setTimeout(() => {
          if (viewedRef.current) return;
          viewedRef.current = true;
          try {
            track("your_cricket_card_viewed", {
              is_linked: counts.is_linked,
              empty: counts.empty,
              matches: counts.matches,
              practices: counts.practices,
              videos: counts.videos,
              conversations: counts.conversations,
              lessons: counts.lessons,
            });
          } catch { /* noop */ }
        }, 1000);
      } else if (!on && timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => {
      if (timer) window.clearTimeout(timer);
      io.disconnect();
    };
  }, [loaded, counts.is_linked, counts.empty, counts.matches,
      counts.practices, counts.videos, counts.conversations, counts.lessons]);

  const isEmpty = counts.empty || (
    counts.matches + counts.practices + counts.videos +
    counts.conversations + counts.lessons === 0
  );

  // Iter XX.160 — De-emphasise the empty card for brand-new visitors.
  // Callers on the landing pass `hideWhenEmpty` so a fresh visitor
  // sees promise → problem → action first, and doesn't waste hero
  // real estate on 0/0/0/0/0. Existing users with meaningful history
  // continue to see the card exactly as before. We wait for the fetch
  // to complete (`loaded`) so we don't briefly render + collapse.
  if (hideWhenEmpty && loaded && isEmpty) return null;

  return (
    <div
      ref={cardRef}
      data-testid="your-cricket-card"
      className="rounded-2xl border border-neutral-800 bg-neutral-900/60 px-4 py-4 mb-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div
          data-testid="your-cricket-card-title"
          className="text-[11px] uppercase tracking-[0.14em] text-emerald-400/90 font-semibold"
        >
          Your Cricket
        </div>
        {counts.is_linked && !isEmpty ? (
          <div
            data-testid="your-cricket-card-status-linked"
            className="text-[11px] text-neutral-500"
          >
            Building
          </div>
        ) : null}
      </div>

      <div
        data-testid="your-cricket-card-metrics"
        className="grid grid-cols-3 gap-x-3 gap-y-3 sm:grid-cols-5"
      >
        <Metric label="Matches" value={counts.matches} testid="matches" />
        <Metric label="Practices" value={counts.practices} testid="practices" />
        <Metric label="Videos" value={counts.videos} testid="videos" />
        <Metric label="Conversations" value={counts.conversations} testid="conversations" />
        <Metric label="Lessons" value={counts.lessons} testid="lessons" />
      </div>

      {isEmpty ? (
        <div
          data-testid="your-cricket-card-empty-state"
          className="mt-4 pt-3 border-t border-neutral-900 text-[13px] leading-snug text-neutral-400"
        >
          <div className="text-neutral-300">Nothing built yet.</div>
          <div className="mt-1 text-neutral-500">
            Your first conversation begins the picture.
          </div>
        </div>
      ) : counts.current_focus ? (
        <div
          data-testid="your-cricket-card-current-focus"
          className="mt-4 pt-3 border-t border-neutral-900 text-[13px] leading-snug"
        >
          <div className="text-neutral-500 text-[11px] uppercase tracking-wider mb-1">
            Working on
          </div>
          <div className="text-neutral-200">{counts.current_focus}</div>
        </div>
      ) : null}
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div data-testid={`your-cricket-metric-${testid}`} className="min-w-0">
      <div
        data-testid={`your-cricket-metric-${testid}-value`}
        className="text-[22px] leading-none font-semibold tabular-nums text-neutral-100"
      >
        {value}
      </div>
      <div
        data-testid={`your-cricket-metric-${testid}-label`}
        className="mt-1 text-[11px] leading-tight text-neutral-500 truncate"
      >
        {label}
      </div>
    </div>
  );
}
